import React, {
  useMemo,
  useState,
} from "react";

import {
  childrensBookIllustrationsByUnit,
  currentChildrensBookCover,
} from "../utils/childrensBookAssetState";
import {
  findOversizedManuscriptUnits,
} from "../utils/childrensBookManuscriptCapacity";
import {
  plainTextFromManuscriptEntry,
} from "../utils/childrensBookManuscriptContent";

function clean(value) {
  return String(value ?? "").trim();
}

function unitRequiresText(unit) {
  return (
    clean(
      unit?.textAmount
    ).toLowerCase() !== "none"
  );
}

function trimValue(visualFormat = {}) {
  const value = clean(visualFormat.trimSize);

  if (value === "custom_dimensions") {
    return clean(visualFormat.customTrimSize);
  }

  return value === "not_decided" ? "" : value;
}

export function buildPublicationHandoffSummary(
  projectPlan = {},
  childrensBookAssets = []
) {
  const units = Array.isArray(
    projectPlan.bookMap?.units
  )
    ? projectPlan.bookMap.units
    : [];

  const manuscripts = Array.isArray(
    projectPlan.manuscriptPlan?.entries
  )
    ? projectPlan.manuscriptPlan.entries
    : [];

  const completedManuscripts = new Set(
    manuscripts
      .filter(
        (entry) =>
          clean(
            plainTextFromManuscriptEntry(
              entry
            )
          ) &&
          entry.status === "complete"
      )
      .map((entry) => clean(entry.unitId))
      .filter(Boolean)
  );

  const manuscriptSatisfiedUnits =
    units.filter(
      (unit) =>
        !unitRequiresText(unit) ||
        completedManuscripts.has(
          unit.id
        )
    );

  const oversizedManuscriptUnits =
    findOversizedManuscriptUnits(
      projectPlan
    );

  const manuscriptLayoutReady =
    oversizedManuscriptUnits
      .length === 0;

  const requiredIllustrations = units.filter(
    (unit) =>
      clean(unit.illustrationPlan) &&
      unit.illustrationPlan !== "none"
  );

  const illustrationsByUnit =
    childrensBookIllustrationsByUnit(
      childrensBookAssets
    );

  const attachedIllustrations =
    requiredIllustrations.filter(
      (unit) =>
        Boolean(
          illustrationsByUnit[unit.id]
        )
    );

  const activeCover =
    currentChildrensBookCover(
      childrensBookAssets,
      "front_cover"
    );

  const title =
    clean(projectPlan.identity?.title) ||
    clean(projectPlan.coverPlan?.title);

  const author =
    clean(projectPlan.identity?.penName) ||
    clean(projectPlan.identity?.authorName) ||
    clean(projectPlan.coverPlan?.authorName);

  const language =
    clean(projectPlan.identity?.language);

  const trimSize =
    trimValue(projectPlan.visualFormat);

  const publicationFormat = clean(
    projectPlan.visualFormat
      ?.publicationFormat
  );

  const identityReady =
    Boolean(title && author);

  const formatReady = Boolean(
    trimSize &&
      publicationFormat &&
      publicationFormat !== "not_decided"
  );

  const structureReady =
    units.length > 0;

  const manuscriptReady =
    structureReady &&
    manuscriptLayoutReady &&
    manuscriptSatisfiedUnits.length ===
      units.length;

  const coverReady =
    Boolean(activeCover);

  const illustrationsReady =
    requiredIllustrations.length === 0 ||
    attachedIllustrations.length ===
      requiredIllustrations.length;

  const ready = [
    identityReady,
    formatReady,
    structureReady,
    manuscriptReady,
    coverReady,
    illustrationsReady,
  ].every(Boolean);

  return {
    title,
    author,
    language,
    trimSize,
    publicationFormat,
    units,
    completedManuscriptCount:
      manuscriptSatisfiedUnits.length,
    requiredIllustrations,
    attachedIllustrationCount:
      attachedIllustrations.length,
    illustrationsByUnit,
    activeCover,
    identityReady,
    formatReady,
    structureReady,
    manuscriptReady,
    manuscriptLayoutReady,
    oversizedManuscriptUnits,
    coverReady,
    illustrationsReady,
    ready,
  };
}

function statusStyle(ready) {
  return {
    display: "inline-flex",
    padding: "5px 9px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    background: ready
      ? "#dcfce7"
      : "#ffedd5",
    color: ready
      ? "#166534"
      : "#9a3412",
  };
}

function languageLabel(value, isFR) {
  const normalized =
    clean(value).toLowerCase();

  if (normalized === "en") {
    return isFR ? "Anglais" : "English";
  }

  if (normalized === "fr") {
    return "Français";
  }

  return clean(value) || (
    isFR ? "Non indiquée" : "Not provided"
  );
}

function formatLabel(value, isFR) {
  const labels = {
    print: isFR ? "Imprimé" : "Print",
    digital: isFR
      ? "Numérique"
      : "Digital",
    print_and_digital: isFR
      ? "Imprimé et numérique"
      : "Print and digital",
  };

  return (
    labels[clean(value)] ||
    clean(value) ||
    (isFR ? "Non indiqué" : "Not provided")
  );
}

export default function ChildrensBookPublicationHandoff({
  lang = "en",
  projectPlan = {},
  childrensBookAssets = [],
  onOpenPublicationProof,
  onOpenBookAssembly,
  onOpenProjectPlan,
  onOpenCoverPlan,
  onOpenManuscriptPlan,
  onOpenIllustrationPlan,
  onPublish,
  onOpenPublishedBook,
  onReturn,
  onExit,
}) {
  const isFR =
    String(lang).toLowerCase() === "fr";

  const summary = useMemo(
    () =>
      buildPublicationHandoffSummary(
        projectPlan,
        childrensBookAssets
      ),
    [projectPlan, childrensBookAssets]
  );

  const publicationRecord =
    projectPlan.publicationReadiness || {};

  const publishedOnNextScenes =
    publicationRecord.status ===
      "published_nextscenes";

  const publishedAt =
    clean(
      publicationRecord.publishedAt
    );

  const [
    publishConfirmationOpen,
    setPublishConfirmationOpen,
  ] = useState(false);

  const [
    publishing,
    setPublishing,
  ] = useState(false);

  const [
    publishError,
    setPublishError,
  ] = useState("");

  async function confirmPublishOnNextScenes() {
    if (
      !summary.ready ||
      typeof onPublish !==
        "function"
    ) {
      return;
    }

    try {
      setPublishing(true);
      setPublishError("");

      const result =
        await onPublish();

      if (result?.ok === false) {
        throw new Error(
          result.error ||
          (
            isFR
              ? "Le livre n’a pas pu être publié."
              : "The book could not be published."
          )
        );
      }

      setPublishConfirmationOpen(
        false
      );

      if (
        typeof onOpenPublishedBook ===
          "function"
      ) {
        onOpenPublishedBook();
      }
    } catch (error) {
      setPublishError(
        clean(error?.message) ||
        (
          isFR
            ? "Le livre n’a pas pu être publié."
            : "The book could not be published."
        )
      );
    } finally {
      setPublishing(false);
    }
  }

  const checks = [
    {
      title: isFR
        ? "Identité du livre"
        : "Book Identity",
      ready: summary.identityReady,
      detail: summary.identityReady
        ? isFR
          ? "Le titre et le nom public de l’auteur sont présents."
          : "The title and public author name are present."
        : isFR
          ? "Ajoutez le titre et le nom public de l’auteur."
          : "Add the title and public author name.",
      onOpen: onOpenProjectPlan,
      action: isFR
        ? "Ouvrir le plan du projet"
        : "Open Project Plan",
    },
    {
      title: isFR
        ? "Format prévu"
        : "Planned Format",
      ready: summary.formatReady,
      detail: summary.formatReady
        ? isFR
          ? "Les dimensions et le format de publication sont définis."
          : "Trim size and publication format are defined."
        : isFR
          ? "Définissez les dimensions et le format de publication."
          : "Define the trim size and publication format.",
      onOpen: onOpenProjectPlan,
      action: isFR
        ? "Vérifier le format"
        : "Review Format",
    },
    {
      title: isFR
        ? "Structure du livre"
        : "Book Structure",
      ready: summary.structureReady,
      detail: isFR
        ? `${summary.units.length} unité(s) dans le Plan du livre.`
        : `${summary.units.length} unit(s) in the Book Map.`,
      onOpen: onOpenBookAssembly,
      action: isFR
        ? "Ouvrir l’assemblage"
        : "Open Book Assembly",
    },
    {
      title: isFR
        ? "Manuscrit"
        : "Manuscript",
      ready: summary.manuscriptReady,
      detail:
        !summary
          .manuscriptLayoutReady
          ? (
              isFR
                ? `${summary.oversizedManuscriptUnits.length} page(s) ou double(s) page(s) contiennent trop de texte et doivent être divisées.`
                : `${summary.oversizedManuscriptUnits.length} page or spread contains too much text and needs to be divided.`
            )
          : (
              isFR
                ? `${summary.completedManuscriptCount} sur ${summary.units.length} unité(s) terminée(s).`
                : `${summary.completedManuscriptCount} of ${summary.units.length} unit(s) complete.`
            ),
      onOpen: onOpenManuscriptPlan,
      action: isFR
        ? "Ouvrir le manuscrit"
        : "Open Manuscript",
    },
    {
      title: isFR
        ? "Fichier de couverture"
        : "Cover File",
      ready: summary.coverReady,
      detail: summary.activeCover
        ? summary.activeCover.fileName ||
          (isFR
            ? "Couverture active enregistrée"
            : "Active cover saved")
        : isFR
          ? "Aucune couverture active n’est jointe."
          : "No active cover is attached.",
      onOpen: onOpenCoverPlan,
      action: isFR
        ? "Ouvrir la couverture"
        : "Open Cover",
    },
    {
      title: isFR
        ? "Fichiers d’illustration"
        : "Illustration Files",
      ready: summary.illustrationsReady,
      detail: isFR
        ? `${summary.attachedIllustrationCount} sur ${summary.requiredIllustrations.length} illustration(s) requise(s) jointe(s).`
        : `${summary.attachedIllustrationCount} of ${summary.requiredIllustrations.length} required illustration(s) attached.`,
      onOpen: onOpenIllustrationPlan,
      action: isFR
        ? "Ouvrir les illustrations"
        : "Open Illustrations",
    },
  ];

  const blockers =
    checks.filter(
      (check) => !check.ready
    ).length;

  const inventory = [
    [
      isFR ? "Titre" : "Title",
      summary.title ||
        (isFR
          ? "Non indiqué"
          : "Not provided"),
    ],
    [
      isFR
        ? "Nom public de l’auteur"
        : "Public author name",
      summary.author ||
        (isFR
          ? "Non indiqué"
          : "Not provided"),
    ],
    [
      isFR ? "Langue" : "Language",
      languageLabel(
        summary.language,
        isFR
      ),
    ],
    [
      isFR
        ? "Format de publication"
        : "Publication format",
      formatLabel(
        summary.publicationFormat,
        isFR
      ),
    ],
    [
      isFR ? "Dimensions" : "Trim size",
      summary.trimSize ||
        (isFR
          ? "Non indiquées"
          : "Not provided"),
    ],
    [
      isFR
        ? "Unités du livre"
        : "Book units",
      summary.units.length,
    ],
  ];

  const externalTasks = [
    isFR
      ? "Révision éditoriale et correction finale"
      : "Editorial review and final proofreading",
    isFR
      ? "Mise en page intérieure et typographie finales"
      : "Final interior layout and typography",
    isFR
      ? "Vérification des droits, crédits et autorisations"
      : "Rights, credits, and permissions review",
    isFR
      ? "ISBN, métadonnées et catégories de la plateforme choisie"
      : "ISBN, metadata, and categories for the chosen platform",
    isFR
      ? "Épreuve finale imprimée ou numérique"
      : "Final print or digital proof",
  ];

  return (
    <section
      aria-label={
        isFR
          ? "Transmission pour publication"
          : "Publication Handoff"
      }
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 16,
          flexWrap: "wrap",
          marginBottom: 18,
        }}
      >
        <div>
          <div
            style={{
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: "0.12em",
              textTransform: "uppercase",
              color: "#b45309",
            }}
          >
            NextScenes Book Blueprint ·{" "}
            {isFR
              ? "Transmission pour publication"
              : "Publication Handoff"}
          </div>

          <h2
            className="ns-section-title"
            style={{ margin: "5px 0 8px" }}
          >
            {isFR
              ? "Préparez le livre pour l’étape extérieure suivante"
              : "Prepare the Book for Its Next External Step"}
          </h2>

          <p
            className="ns-card-meta"
            style={{
              margin: 0,
              maxWidth: 840,
              lineHeight: 1.6,
            }}
          >
            {isFR
              ? "Cette vue rassemble les informations et fichiers déjà enregistrés pour la prochaine étape extérieure."
              : "This view gathers the saved information and files for the next external step."}
          </p>
        </div>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onExit}
        >
          {isFR
            ? "Retour à Mes projets"
            : "Return to My Projects"}
        </button>
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        style={{
          padding: 20,
          borderRadius: 18,
          marginBottom: 18,
          border: summary.ready
            ? "1px solid rgba(22,101,52,0.24)"
            : "1px solid rgba(154,52,18,0.24)",
        }}
      >
        <h3
          className="ns-card-title"
          style={{ margin: 0 }}
        >
          {summary.ready
            ? isFR
              ? "Matériel prêt pour la préparation extérieure"
              : "Material Ready for External Preparation"
            : isFR
              ? "Transmission incomplète"
              : "Handoff Incomplete"}
        </h3>

        <span
          style={{
            ...statusStyle(summary.ready),
            marginTop: 10,
          }}
        >
          {summary.ready
            ? isFR
              ? "Prêt"
              : "Ready"
            : isFR
              ? `${blockers} élément(s) à compléter`
              : `${blockers} item(s) need attention`}
        </span>
      </div>

      <div
        className="ns-nbb-summary-grid"
        style={{
          display: "grid",
          gridTemplateColumns:
            "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 12,
          marginBottom: 20,
        }}
      >
        {inventory.map(([label, value]) => (
          <div
            key={label}
            className="ns-card ns-nbb-summary-card"
            style={{
              padding: 16,
              borderRadius: 14,
            }}
          >
            <div
              style={{
                color: "#64748b",
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {label}
            </div>

            <div
              style={{
                marginTop: 6,
                color: "#1e293b",
                fontWeight: 500,
              }}
            >
              {value}
            </div>
          </div>
        ))}
      </div>

      <div
        className="ns-nbb-major-grid"
        style={{ display: "grid", gap: 14 }}
      >
        {checks.map((check) => (
          <article
            key={check.title}
            className="ns-card ns-nbb-major-panel"
            style={{
              padding: 18,
              borderRadius: 16,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent:
                  "space-between",
                gap: 12,
                flexWrap: "wrap",
              }}
            >
              <div>
                <h3
                  className="ns-card-title"
                  style={{ margin: 0 }}
                >
                  {check.title}
                </h3>

                <p
                  className="ns-card-meta"
                  style={{
                    margin: "7px 0 0",
                    lineHeight: 1.55,
                  }}
                >
                  {check.detail}
                </p>
              </div>

              <span
                style={statusStyle(
                  check.ready
                )}
              >
                {check.ready
                  ? isFR
                    ? "Présent"
                    : "Present"
                  : isFR
                    ? "À compléter"
                    : "Needs attention"}
              </span>
            </div>

            {!check.ready &&
            typeof check.onOpen ===
              "function" ? (
              <button
                className="ns-button ns-button-outline"
                type="button"
                onClick={check.onOpen}
                style={{ marginTop: 14 }}
              >
                {check.action}
              </button>
            ) : null}
          </article>
        ))}
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        style={{
          padding: 18,
          marginTop: 20,
          borderRadius: 16,
        }}
      >
        <h3
          className="ns-card-title"
          style={{ margin: 0 }}
        >
          {isFR
            ? "Inventaire des fichiers actifs"
            : "Active File Inventory"}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: "10px 0 0",
            lineHeight: 1.55,
          }}
        >
          {summary.activeCover
            ? summary.activeCover.fileName ||
              (isFR
                ? "Couverture active"
                : "Active cover")
            : isFR
              ? "Aucun fichier de couverture actif"
              : "No active cover file"}
        </p>

        {summary.requiredIllustrations.map(
          (unit) => {
            const asset =
              summary.illustrationsByUnit[
                unit.id
              ];

            return (
              <p
                key={unit.id}
                className="ns-card-meta"
                style={{
                  margin: "8px 0 0",
                  lineHeight: 1.55,
                }}
              >
                {clean(unit.workingTitle) ||
                  clean(unit.pageNumber) ||
                  (isFR
                    ? "Unité sans titre"
                    : "Untitled unit")}
                {" · "}
                {asset
                  ? asset.fileName ||
                    (isFR
                      ? "Illustration active"
                      : "Active illustration")
                  : isFR
                    ? "Illustration requise non jointe"
                    : "Required illustration not attached"}
              </p>
            );
          }
        )}
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        style={{
          padding: 18,
          marginTop: 20,
          borderRadius: 16,
        }}
      >
        <h3
          className="ns-card-title"
          style={{ margin: 0 }}
        >
          {isFR
            ? "Tâches extérieures restant sous le contrôle de l’auteur"
            : "External Tasks Remaining Under Author Control"}
        </h3>

        <ul
          style={{
            margin: "14px 0 0",
            paddingLeft: 22,
            display: "grid",
            gap: 9,
            fontWeight: 400,
          }}
        >
          {externalTasks.map((task) => (
            <li key={task}>{task}</li>
          ))}
        </ul>
      </div>

      <div
        className="ns-card"
        style={{
          padding: 16,
          marginTop: 18,
          borderRadius: 16,
          background: "#fff7ed",
          border:
            "1px solid rgba(234,88,12,0.25)",
        }}
      >
        <strong style={{ fontWeight: 700 }}>
          {isFR
            ? "Guide de transmission uniquement"
            : "Handoff guide only"}
        </strong>

        <p
          className="ns-card-meta"
          style={{
            margin: "7px 0 0",
            lineHeight: 1.55,
          }}
        >
          {isFR
            ? "Cette vue ne crée ni PDF ni dossier téléchargeable, ne contacte aucune plateforme et ne publie rien automatiquement."
            : "This view creates no PDF or downloadable package, contacts no platform, and publishes nothing automatically."}
        </p>

        <p
          className="ns-card-meta"
          style={{
            margin: "7px 0 0",
            lineHeight: 1.55,
          }}
        >
          {isFR
            ? "Créativité humaine d’abord. Assistance IA ensuite. Décision finale de l’auteur."
            : "Human creativity first. AI assistance second. Author decision final."}
        </p>
      </div>

      <div
        className="ns-card ns-nbb-major-panel"
        style={{
          padding: 18,
          marginTop: 20,
          borderRadius: 16,
          border: publishedOnNextScenes
            ? "2px solid rgba(22,101,52,0.34)"
            : "2px solid rgba(22,101,52,0.26)",
          background: publishedOnNextScenes
            ? "#ecfdf5"
            : "#f7fff9",
        }}
      >
        <h3
          className="ns-card-title"
          style={{ margin: 0 }}
        >
          {publishedOnNextScenes
            ? (
                isFR
                  ? "Publié sur NextScenes"
                  : "Published on NextScenes"
              )
            : (
                isFR
                  ? "Publier sur NextScenes"
                  : "Publish on NextScenes"
              )}
        </h3>

        <p
          className="ns-card-meta"
          style={{
            margin: "9px 0 0",
            lineHeight: 1.55,
          }}
        >
          {publishedOnNextScenes
            ? (
                isFR
                  ? "Ce livre est marqué comme publié dans NextScenes. Aucune librairie, imprimerie ou plateforme extérieure n’a été contactée."
                  : "This book is marked as published inside NextScenes. No retailer, printer, or external platform was contacted."
              )
            : (
                isFR
                  ? "Cette action marque le livre terminé comme publié dans NextScenes. Elle ne crée aucun PDF et ne contacte aucune plateforme extérieure."
                  : "This action marks the completed book as published inside NextScenes. It creates no PDF and contacts no external platform."
              )}
        </p>

        {publishedOnNextScenes &&
        publishedAt ? (
          <p
            className="ns-card-meta"
            style={{
              margin: "9px 0 0",
              fontWeight: 700,
            }}
          >
            {isFR
              ? `Publié le ${publishedAt}`
              : `Published ${publishedAt}`}
          </p>
        ) : null}

        {publishedOnNextScenes &&
        typeof onOpenPublishedBook ===
          "function" ? (
          <button
            className="ns-button ns-button-primary"
            type="button"
            onClick={
              onOpenPublishedBook
            }
            style={{
              marginTop: 14,
              minWidth: 190,
            }}
          >
            {isFR
              ? "Ouvrir le livre publié"
              : "Open Published Book"}
          </button>
        ) : null}

        {!publishedOnNextScenes ? (
          <button
            className="ns-button ns-button-primary"
            type="button"
            disabled={
              !summary.ready ||
              publishing ||
              typeof onPublish !==
                "function"
            }
            onClick={() => {
              setPublishError("");
              setPublishConfirmationOpen(
                true
              );
            }}
            style={{
              marginTop: 14,
              minWidth: 190,
            }}
          >
            {isFR
              ? "Publier sur NextScenes"
              : "Publish on NextScenes"}
          </button>
        ) : null}

        {publishError ? (
          <p
            role="alert"
            style={{
              margin: "12px 0 0",
              color: "#991b1b",
              fontWeight: 700,
            }}
          >
            {publishError}
          </p>
        ) : null}
      </div>

      <div
        style={{
          marginTop: 18,
          display: "flex",
          gap: 10,
          flexWrap: "wrap",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={
            onOpenPublicationProof
          }
        >
          {isFR
            ? "Retour à l’épreuve de publication"
            : "Return to Publication Proof"}
        </button>

        <button
          className="ns-button ns-button-outline"
          type="button"
          onClick={onReturn}
        >
          {isFR
            ? "Retour au NextScenes Book Blueprint"
            : "Return to NextScenes Book Blueprint"}
        </button>
      </div>
      {publishConfirmationOpen ? (
        <div
          role="presentation"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "grid",
            placeItems: "center",
            padding: 20,
            background:
              "rgba(15,23,42,0.56)",
          }}
          onMouseDown={(event) => {
            if (
              event.target ===
              event.currentTarget &&
              !publishing
            ) {
              setPublishConfirmationOpen(
                false
              );
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label={
              isFR
                ? "Confirmer la publication"
                : "Confirm publication"
            }
            className="ns-card"
            style={{
              width:
                "min(560px, 100%)",
              padding: 22,
              borderRadius: 18,
              background: "#ffffff",
              border:
                "2px solid rgba(22,101,52,0.32)",
              boxShadow:
                "0 24px 60px rgba(15,23,42,0.26)",
            }}
          >
            <h3
              className="ns-card-title"
              style={{ margin: 0 }}
            >
              {isFR
                ? "Publier ce livre sur NextScenes ?"
                : "Publish this book on NextScenes?"}
            </h3>

            <p
              className="ns-card-meta"
              style={{
                margin: "12px 0 0",
                lineHeight: 1.6,
              }}
            >
              {isFR
                ? "Le livre sera marqué comme publié dans NextScenes. Cette action ne crée aucun PDF, ne soumet rien à Amazon ou KDP et ne contacte aucune plateforme extérieure."
                : "The book will be marked as published inside NextScenes. This does not create a PDF, submit anything to Amazon or KDP, or contact any external platform."}
            </p>

            <div
              style={{
                display: "flex",
                gap: 10,
                flexWrap: "wrap",
                marginTop: 18,
              }}
            >
              <button
                className="ns-button ns-button-outline"
                type="button"
                disabled={publishing}
                onClick={() =>
                  setPublishConfirmationOpen(
                    false
                  )
                }
              >
                {isFR
                  ? "Annuler"
                  : "Cancel"}
              </button>

              <button
                className="ns-button ns-button-primary"
                type="button"
                disabled={publishing}
                aria-busy={publishing}
                onClick={
                  confirmPublishOnNextScenes
                }
              >
                {publishing
                  ? (
                      isFR
                        ? "Publication…"
                        : "Publishing…"
                    )
                  : (
                      isFR
                        ? "Confirmer et publier"
                        : "Confirm and Publish"
                    )}
              </button>
            </div>
          </div>
        </div>
      ) : null}

    </section>
  );
}
